import * as React from 'react';
import * as microsoftTeams from "@microsoft/teams-js";
import { Alert, Button, Flex, FlexItem, Image, Loader, Text } from '@fluentui/react-northstar';
import { AcceptIcon, FilesEmptyIcon, FilesUploadIcon, FilesPdfIcon, WordIcon, ExcelIcon, FilesPictureColoredIcon } from '@fluentui/react-icons-northstar';
import { uploadQueueAttachment } from '../../api/publicapp-api';
import Dropzone from 'react-dropzone';
import { RouteComponentProps } from 'react-router-dom';
import { withTranslation, WithTranslation } from "react-i18next";
import { TFunction } from "i18next";
import './upload-document.scss';

export interface IUploadDocumentState {
    files: any[],
    loading: boolean,
    uploading: boolean,
    sentDocument: boolean,
    message: string | null,
}

export type IUploadDocumentProps = {} & RouteComponentProps & WithTranslation;

class UploadDocument extends React.Component<IUploadDocumentProps, IUploadDocumentState> {
    private localize: TFunction;
    searchParam?: any = null;
    data?: any = null;

    constructor(props: IUploadDocumentProps) {
        super(props);
        this.localize = this.props.t;

        this.state = {
            files: [],
            loading: false,
            uploading: false,
            sentDocument: false,
            message: null,
        };

        this.searchParam = new URLSearchParams(window.location.search);
        this.data = JSON.parse(window.atob(this.searchParam.get("data")));
    }

    public componentDidMount() {
        microsoftTeams.initialize();
    }

    private render_errorMesage(): JSX.Element {
        if (this.state.message != "") {
            return (<Alert danger content={this.state.message} />);
        }
        else {
            return (<div />);
        }
    }

    public renderFiles = (file: any) => {
        let icon = null;

        if (file) {
            if (file.name?.toLowerCase().includes("pdf"))
                icon = <FilesPdfIcon />
            else if (file.name?.toLowerCase().includes("doc"))
                icon = <WordIcon />
            else if (file.name?.toLowerCase().includes("xls"))
                icon = <ExcelIcon />
            else
                icon = <FilesPictureColoredIcon />

            return (
                <Flex gap="gap.medium" hAlign="center">
                    {icon}
                    <Text content={file.name} />
                    <Text className="size" content={`(${this.formatBytes(file.size)})`} />
                </Flex>
            );
        }
    }

    private formatBytes = (bytes: number) => {
        var units = ['B', 'KB', 'MB', 'GB', 'TB'],
            i;

        for (i = 0; bytes >= 1024 && i < 4; i++) {
            bytes /= 1024;
        }

        return bytes.toFixed(2) + units[i];
    }


    private acceptedFile(acceptedFiles: any, context_obj: any) {
        if (acceptedFiles.length && this.state.files.length < 3) {
            let files = this.state.files;

            files.push(acceptedFiles[0]);

            this.setState({
                loading: true,
                files: files,
            })
        }
    }

    private onUpload = async () => {
        this.setState({
            uploading: true,
        });

        var formData = new FormData();

        if (this.state.files[0]) formData.append("file", this.state.files[0]);
        if (this.state.files[1]) formData.append("file2", this.state.files[1]);
        if (this.state.files[2]) formData.append("file3", this.state.files[2]);

        let response = await uploadQueueAttachment(formData, this.data.meetingQueueId, this.data.serviceUrl, this.data.activityId);

        if (response.status === 200 && response.data) {
            this.setState({
                loading: false,
                uploading: false,
                sentDocument: true,
            })
        }
    }

    private onSubmit = () => {
        if (this.data != null) {
            microsoftTeams.tasks.submitTask();
        }
    }

    private renderDropZone(): JSX.Element {
        const thumbs = this.state?.files != null ? this.state.files?.map((file: any) => (
            <div key={file.name}>
                <Flex className="file" gap="gap.large" hAlign="center" vAlign="center" column>
                    {this.renderFiles(file)}
                </Flex>
            </div>
        )) : "";

        return (
            <Dropzone
                accept={{
                    'image/png': ['.png'],
                    'image/jpg': ['.jpg', '.jpeg'],
                    'document/excel': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'],
                    'document/pdf': ['application/pdf'],
                    'document/word': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword']
                }}
                maxSize={5000000}
                maxFiles={3}
                onDrop={acceptedFiles =>
                    this.acceptedFile(acceptedFiles, this)
                }
            >
                {({
                    getRootProps,
                    getInputProps,
                    isDragActive,
                    isDragAccept,
                    isDragReject
                }) => {
                    const additionalClass = isDragAccept
                        ? "accept"
                        : isDragReject
                            ? "reject"
                            : "";

                    return (
                        <section>
                            <div {...getRootProps({ className: `dropzone ${additionalClass}` })}>
                                <input {...getInputProps()} />
                                <p className="file-text">Arraste e solte um arquivo aqui ou clique para selecionar o arquivo.</p>
                                <aside>
                                    {thumbs}
                                </aside>
                            </div>
                        </section>
                    );
                }}
            </Dropzone>
        );
    }

    private renderMain(): JSX.Element {
        return (
            <Flex gap="gap.medium" vAlign="center" hAlign="center" className="container" column fill>
                {!this.state.sentDocument && 
                    <FlexItem>
                        {this.renderDropZone()}
                    </FlexItem>
                }
                {this.state.sentDocument && 
                    <FlexItem>
                        <Flex gap="gap.medium" vAlign="center" hAlign="center" column>
                            <Image src="/images/done.svg" />
                            <Text content="Arquivo(s) enviado(s) com sucesso!" />
                        </Flex>
                    </FlexItem>
                }
                <FlexItem>
                    <Flex gap="gap.large" hAlign="center">
                        <Button
                            className={(this.state.sentDocument || this.state.uploading) ? "readonly" : ""}
                            content="Enviar"
                            icon={this.state.sentDocument ? <AcceptIcon /> : <FilesUploadIcon />}
                            iconPosition="after"
                            onClick={this.onUpload}
                            disabled={!this.state.files.length}
                            loading={this.state.uploading}
                            primary={this.state.sentDocument ? false : true}
                            hidden={this.state.sentDocument}
                        />

                        <Button
                            content="Fechar"
                            className={this.state.sentDocument ? "clear" : ""}
                            onClick={this.onSubmit}
                            disabled={!this.state.files.length || !this.state.sentDocument}
                        />

                        <Button
                            content="Limpar"
                            onClick={() => this.setState({ files: [], sentDocument: false })}
                            disabled={!this.state.files.length || this.state.sentDocument}
                            hidden={this.state.sentDocument}
                        />
                    </Flex>
                </FlexItem>
            </Flex>
        );
    }

    public render(): JSX.Element {
        return (
            <Flex padding="padding.medium" hAlign="center" vAlign="center" className="document-container">
                {this.renderMain()}
            </Flex>
        );
    }
}

const inMeetingPanelWithTranslation = withTranslation()(UploadDocument);
export default inMeetingPanelWithTranslation;
